import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ChevronRight, Bot } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { SUPPORTED_LANGS } from "@/i18n/translations";
import StatusBadge from "@/components/common/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function TicketsPage() {
  const { t, lang } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", system: "access_control", priority: "normal", language: lang });

  const load = useCallback(() => {
    return api.get("/tickets").then(r => setTickets(r.data)).catch(() => setTickets([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tickets", form);
      toast.success("AI response generated.");
      setOpen(false);
      setForm({ subject: "", body: "", system: "access_control", priority: "normal", language: lang });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 lg:p-10 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-overline">03 · AI Response Engine</div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-white mt-2" data-testid="tickets-title">{t("tickets.title")}</h1>
          <p className="mt-2 text-sm text-secondary-foreground/70 flex items-center gap-2"><Bot className="w-4 h-4 text-primary" /> Gemini 3 Flash · 5 languages</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="inline-flex items-center gap-2 px-5 h-10 rounded-sm bg-primary text-primary-foreground font-medium hover:bg-[#33B9FF] transition-colors" data-testid="tickets-new-button">
              <Plus className="w-4 h-4" /> {t("tickets.new")}
            </button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-xl">
            <DialogHeader><DialogTitle className="text-white font-mono tracking-widest uppercase text-sm">{t("tickets.new")}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="ticket-form">
              <F label={t("tickets.form.subject")}><input required value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className={inp} data-testid="ticket-subject-input" /></F>
              <F label={t("tickets.form.body")}><textarea required rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} className={`${inp} h-auto py-2 leading-relaxed`} data-testid="ticket-body-input" /></F>
              <div className="grid grid-cols-3 gap-3">
                <F label={t("tickets.form.system")}><select value={form.system} onChange={e => setForm({ ...form, system: e.target.value })} className={inp} data-testid="ticket-system-select"><option value="access_control">Access Control</option><option value="vms">VMS</option><option value="radar">Radar</option></select></F>
                <F label={t("tickets.form.priority")}><select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className={inp} data-testid="ticket-priority-select"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="critical">Critical</option></select></F>
                <F label={t("tickets.form.language")}><select value={form.language} onChange={e => setForm({ ...form, language: e.target.value })} className={inp} data-testid="ticket-language-select">{SUPPORTED_LANGS.map(l => <option key={l.code} value={l.code}>{l.short}</option>)}</select></F>
              </div>
              <DialogFooter>
                <button type="submit" disabled={busy} className="h-10 px-5 rounded-sm bg-primary text-primary-foreground font-medium disabled:opacity-50" data-testid="ticket-form-submit">
                  {busy ? "Generating AI reply…" : t("tickets.form.submit")}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <div className="border border-border rounded-sm bg-card overflow-hidden">
        {tickets.length === 0 ? (
          <div className="p-10 text-sm text-muted-foreground text-center">{t("tickets.empty")}</div>
        ) : tickets.map(tk => (
          <Link key={tk.ticket_id} to={`/tickets/${tk.ticket_id}`} className="flex items-center gap-4 px-6 py-4 border-b border-border/60 last:border-b-0 hover:bg-secondary/40 transition-colors" data-testid={`ticket-row-${tk.ticket_id}`}>
            <div className="w-1 h-10 bg-primary/50 rounded-sm" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-mono tracking-widest text-primary">{tk.ticket_id}</span>
                <span className="text-[10px] font-mono tracking-widest uppercase text-muted-foreground">{tk.language}</span>
                <span className="text-[10px] font-mono tracking-widest uppercase text-muted-foreground">· {tk.priority}</span>
              </div>
              <div className="text-sm text-white mt-1 truncate">{tk.subject}</div>
              <div className="text-[11px] font-mono tracking-widest text-muted-foreground uppercase mt-1">{tk.owner_name} · {(tk.system || "").replace("_", " ")}</div>
            </div>
            <StatusBadge status={tk.status} />
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </Link>
        ))}
      </div>
    </div>
  );
}

const inp = "w-full h-10 px-3 rounded-sm bg-secondary border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none font-mono text-sm text-white";
function F({ label, children }) { return <label className="block"><div className="mb-1.5 text-[10px] font-mono tracking-[0.22em] text-muted-foreground uppercase">{label}</div>{children}</label>; }
